import { CommonModule } from '@angular/common';
import { Component, computed, EventEmitter, inject, Input, model, Output, signal } from '@angular/core';
import { DialogModule } from 'primeng/dialog';
import { StepsModule } from 'primeng/steps';
import { ButtonModule } from 'primeng/button';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { SiteService } from '@/app/plateformebeneficiaire/core/service/site.service';
import { MeetingService } from '@/app/plateformebeneficiaire/core/service/meeting.service';
import { StepperModule } from 'primeng/stepper';
import { IftaLabelModule } from 'primeng/iftalabel';
import { SelectModule } from 'primeng/select';
import { PanelModule } from 'primeng/panel';
import { TableModule } from 'primeng/table';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { InputOtpModule } from 'primeng/inputotp';
import { ExternalLinksService } from '@/app/plateformebeneficiaire/core/service/externalLinks.service';
import { ListExternalApplicationsEnum } from '@/app/plateformebeneficiaire/core/models/enums/listExternalApplications.enum';
import { Dossier, SearchDossierResponse } from '@/app/plateformebeneficiaire/core/models/externalLinks/searchDossiers.model';
import { UserService } from '@/app/plateformebeneficiaire/core';

@Component({
  selector: 'app-create-external-link-dialog',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    StepsModule,
    ButtonModule,
    FormsModule,
    DialogModule,
    StepsModule,
    StepperModule,
    SelectModule,
    IftaLabelModule,
    TableModule,
    PanelModule,
    ToggleButtonModule,
    ProgressSpinnerModule,
    InputTextModule,
    MessageModule,
    InputOtpModule
  ],
  templateUrl: './create-external-link-dialog.component.html',
  styleUrls: ['./create-external-link-dialog.component.scss'],
})
export class CreateExternalLinkDialogComponent {
  @Input() visible: boolean = false;
  @Input() app: string = '';

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() reset = new EventEmitter<void>();
  @Output() created = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  readonly #externalLinkService = inject(ExternalLinksService)
  readonly #userService = inject(UserService)

  verificationMethod: any = null;
  verificationValue: string = '';
  verificationCodeReceived: string = '';
  isVerificationCodeValid: boolean = false;
  listDossiers: Dossier[] = [];
  tokenProof: string = '';

  isInputValid: boolean = false;
  errorMessage: string | null = null;

  activeIndex = signal(1);
  verificationMethods = [
    { key: 'SMS', libelle: 'Par téléphone', description: 'Vous recevrez un code par SMS avant de continuer' },
    { key: 'EMAIL', libelle: 'Par email', description: 'Vous recevrez un code par email avant de continuer' },
  ]

  resendTimer: number = 0;
  resendInterval: any = null;

  goToStep(newStepNumber: number) {
    this.activeIndex.set(newStepNumber);
  }
  closeDialog() {
    this.visible = false;
    this.visibleChange.emit(this.visible);
    this.reset.emit();
  }
  chooseTypeVerification(method: any) {
    this.verificationMethod = method;
    this.verificationValue = '';
    this.isInputValid = false;
  }
  validateInput() {
    if (this.verificationMethod === 'SMS') {
        const phoneRegex = /^\d{10}$/;
        this.isInputValid = phoneRegex.test(this.verificationValue);
        this.errorMessage = this.isInputValid ? null : 'Le numéro de téléphone doit contenir exactement 10 chiffres.';
    } else if (this.verificationMethod === 'EMAIL') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        this.isInputValid = emailRegex.test(this.verificationValue);
        this.errorMessage = this.isInputValid ? null : 'Veuillez entrer une adresse e-mail valide.';
    } else {
        this.isInputValid = false;
        this.errorMessage = null;
    }
  }
  validatorVerificationCode() {
    const codeRegex = /^\d{8}$/; // Ensure the code is exactly 8 digits
    this.isVerificationCodeValid = codeRegex.test(this.verificationCodeReceived);
  }

  startResendTimer() {
    this.resendTimer = 90; // 90 seconds
    this.resendInterval = setInterval(() => {
        if (this.resendTimer > 0) {
            this.resendTimer--;
        } else {
            clearInterval(this.resendInterval);
        }
    }, 1000);
  }

  askVerificationCode() {
    this.#externalLinkService.askVerificationCode.mutate({
      identifiant: this.verificationValue,
      methode: this.verificationMethod,
      logiciel: this.app,
    }, {
      onSuccess: () => {
        this.goToStep(4);
        this.startResendTimer(); // Start the timer after successfully requesting the code
      },
      onError: (error: any) => {
        alert('error')
        // this.messageService.add({ key: 'global', severity: 'error', summary: 'Erreur', detail: error.message });
      }
    });
  }

  validateVerificationCode() {
    this.#externalLinkService.validateVerificationCode.mutate({
      identifiant: this.verificationValue,
      methode: this.verificationMethod,
      code: this.verificationCodeReceived,
      logiciel: this.app,
    }, {
      onSuccess: (datas: SearchDossierResponse) => {  
        if (this.app === ListExternalApplicationsEnum.Ressifnet) {
          let dossiers: Dossier[] = datas.dossiers.map((dossier: any) => ({
            id: dossier.id,
            numeroDossier: dossier.numeroDossier,
            email: dossier.email,
            telephone: `${dossier.telephone1} ${dossier.telephone2 || ''}`.trim(),
            nom: dossier.nom,
            prenom: dossier.prenom,
            description: `Dossier N°${dossier.numeroDossier}`
          }));
          this.listDossiers = dossiers;
          this.tokenProof = datas.verificationTokenProof;
          this.goToStep(5);
        }
      },
      onError: (error: any) => {
        alert('Error occurred');
        console.error('Error:', error);
      }
    });
  }

  createLink(dossier: Dossier) {
    if (this.app === ListExternalApplicationsEnum.Ressifnet) {
        this.#externalLinkService.validateExternalLink.mutate({
          identifiant: this.verificationValue,
          methode: this.verificationMethod,
          code: this.verificationCodeReceived,
          logiciel: this.app,
          verificationTokenProof: this.tokenProof,
          identifiantDossier: dossier.id
      }, {
        onSuccess: (datas: any) => {  
          this.goToStep(6);
        },
        onError: (error: any) => {
          alert('Error occurred');
          console.error('Error:', error);
        }
      });
    }
  }

  handlePaste(event: ClipboardEvent) {
    const pastedData = event.clipboardData?.getData('text') || '';
    if (/^\d{8}$/.test(pastedData)) { // Ensure the pasted data is exactly 8 digits
        this.verificationCodeReceived = pastedData;
        this.validatorVerificationCode();
    }
    event.preventDefault(); // Prevent the default paste behavior
  }

  finalize() {
    this.#userService.reloadAccessTokenInformations.mutate(undefined, {
        onSuccess : () => {
          this.closeDialog();
        }
    });
  }

  colors = [
    '#2563eb',
    '#db2777',
    '#ea580c',
    '#0ceab6'
  ]
}
